// ===========================================
// User Management React Query Hooks
// ===========================================
// Custom hooks for teachers, parents, students, families API operations

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  useInvalidateSlotCaches,
  useInvalidateTeacherRosterCaches,
} from './useSlotCacheInvalidation';
import {
  teachersApi,
  parentsApi,
  studentsApi,
  familiesApi,
  parentProfileApi,
  CreateParentData,
  UpdateParentData,
  UpdateParentProfileData,
} from '../api/users.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const userKeys = {
  all: ['users'] as const,
  teachers: () => [...userKeys.all, 'teachers'] as const,
  teacher: (id: string) => [...userKeys.teachers(), id] as const,
  teacherAvailability: (teacherId: string) =>
    [...userKeys.teacher(teacherId), 'availability'] as const,
  teacherReplacementInvolvement: (teacherId: string) =>
    [...userKeys.teacher(teacherId), 'replacement-involvement'] as const,
  teacherTaskSummary: () => [...userKeys.teachers(), 'task-summary'] as const,
  teacherTaskReset: () => [...userKeys.teachers(), 'task-reset'] as const,
  parents: () => [...userKeys.all, 'parents'] as const,
  parent: (id: string) => [...userKeys.parents(), id] as const,
  parentProfile: () => [...userKeys.all, 'parentProfile'] as const,
  students: () => [...userKeys.all, 'students'] as const,
  student: (id: string) => [...userKeys.students(), id] as const,
  families: () => [...userKeys.all, 'families'] as const,
  familiesAdminList: () => [...userKeys.all, 'families', 'admin-list'] as const,
  family: (id: string) => [...userKeys.families(), id] as const,
  familyImpact: (id: string) => [...userKeys.family(id), 'impact'] as const,
};

// ===========================================
// TEACHERS HOOKS
// ===========================================

export function useTeachers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.teachers(),
    queryFn: teachersApi.getAll,
    enabled: options?.enabled ?? true,
  });
}

export function useTeachersForBooking(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...userKeys.teachers(), 'available-for-booking'],
    queryFn: teachersApi.getAvailableForBooking,
    enabled: options?.enabled ?? true,
  });
}

export function useTeacher(id: string) {
  return useQuery({
    queryKey: userKeys.teacher(id),
    queryFn: () => teachersApi.getById(id),
    enabled: !!id,
  });
}

/**
 * A teacher's per-week replacement involvement (being replaced + covering).
 * The route needs people_teachers.read: a caller without it passes `enabled: false` (#272).
 */
export function useTeacherReplacementInvolvement(id: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.teacherReplacementInvolvement(id || ''),
    queryFn: () => teachersApi.getReplacementInvolvement(id!),
    enabled: !!id && (options?.enabled ?? true),
  });
}

export function useCreateTeacher() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const invalidateRosterCaches = useInvalidateTeacherRosterCaches();

  return useMutation({
    mutationFn: (data: {
      email: string;
      firstName: string;
      lastName: string;
      phone?: string;
      password?: string;
      bio?: string;
      instrumentIds?: string[];
      color?: string;
      workingWithChildrenId?: string;
    }) => teachersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.teachers() });
      invalidateRosterCaches();
    },
    // #588: a failed Add Teacher used to be silent.
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      enqueueSnackbar(error.response?.data?.message || 'Failed to add teacher', {
        variant: 'error',
      });
    },
  });
}

export function useUpdateTeacher() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const invalidateRosterCaches = useInvalidateTeacherRosterCaches();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<{
        email: string;
        firstName: string;
        lastName: string;
        phone: string;
        bio: string;
        isActive: boolean;
        color: string | null;
        workingWithChildrenId: string;
      }>;
    }) => teachersApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.teachers() });
      queryClient.invalidateQueries({ queryKey: userKeys.teacher(variables.id) });
      invalidateRosterCaches();
      enqueueSnackbar('Teacher updated successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to update teacher';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useDeleteTeacher() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const invalidateRosterCaches = useInvalidateTeacherRosterCaches();

  return useMutation({
    mutationFn: (id: string) => teachersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.teachers() });
      invalidateRosterCaches();
      enqueueSnackbar('Teacher deleted successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to delete teacher';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useAssignInstrument() {
  const queryClient = useQueryClient();
  const invalidateRosterCaches = useInvalidateTeacherRosterCaches();

  return useMutation({
    mutationFn: ({
      teacherId,
      instrumentId,
      isPrimary,
    }: {
      teacherId: string;
      instrumentId: string;
      isPrimary?: boolean;
    }) => teachersApi.assignInstrument(teacherId, instrumentId, isPrimary),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.teacher(variables.teacherId) });
      queryClient.invalidateQueries({ queryKey: userKeys.teachers() });
      invalidateRosterCaches();
    },
  });
}

export function useRemoveInstrument() {
  const queryClient = useQueryClient();
  const invalidateRosterCaches = useInvalidateTeacherRosterCaches();

  return useMutation({
    mutationFn: ({
      teacherId,
      instrumentId,
    }: {
      teacherId: string;
      instrumentId: string;
    }) => teachersApi.removeInstrument(teacherId, instrumentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.teacher(variables.teacherId) });
      queryClient.invalidateQueries({ queryKey: userKeys.teachers() });
      invalidateRosterCaches();
    },
  });
}

export function useSetPrimaryInstrument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      teacherId,
      instrumentId,
    }: {
      teacherId: string;
      instrumentId: string;
    }) => teachersApi.setPrimaryInstrument(teacherId, instrumentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.teacher(variables.teacherId) });
      queryClient.invalidateQueries({ queryKey: userKeys.teachers() });
    },
  });
}

// ===========================================
// TEACHER AVAILABILITY HOOKS
// ===========================================

export function useTeacherAvailability(teacherId: string | null) {
  return useQuery({
    queryKey: userKeys.teacherAvailability(teacherId || ''),
    queryFn: () => teachersApi.getAvailability(teacherId!),
    enabled: !!teacherId,
  });
}

// Weekly-availability mutations invalidate the shared slot caches too: the
// hybrid/make-up/extra slot generators all read availability windows, so a
// window change makes any cached slot list stale (previously only the
// window list itself refreshed and slot UIs served stale data for the
// staleTime window).

export function useAddTeacherAvailability() {
  const invalidateSlotCaches = useInvalidateSlotCaches();

  return useMutation({
    mutationFn: ({
      teacherId,
      data,
    }: {
      teacherId: string;
      data: { dayOfWeek: number; startTime: string; endTime: string };
    }) => teachersApi.addAvailability(teacherId, data),
    onSuccess: (_, variables) => {
      invalidateSlotCaches([userKeys.teacherAvailability(variables.teacherId)]);
    },
  });
}

export function useUpdateTeacherAvailability() {
  const invalidateSlotCaches = useInvalidateSlotCaches();

  return useMutation({
    mutationFn: ({
      teacherId,
      slotId,
      data,
    }: {
      teacherId: string;
      slotId: string;
      data: { dayOfWeek?: number; startTime?: string; endTime?: string; isActive?: boolean };
    }) => teachersApi.updateAvailability(teacherId, slotId, data),
    onSuccess: (_, variables) => {
      invalidateSlotCaches([userKeys.teacherAvailability(variables.teacherId)]);
    },
  });
}

export function useAvailabilityAffectedLessons() {
  return useMutation({
    mutationFn: ({
      teacherId,
      slotId,
      proposed,
    }: {
      teacherId: string;
      slotId: string;
      proposed: { dayOfWeek: number; startTime: string; endTime: string } | null;
    }) => teachersApi.getAvailabilityAffectedLessons(teacherId, slotId, proposed),
  });
}

export function useDeleteTeacherAvailability() {
  const invalidateSlotCaches = useInvalidateSlotCaches();

  return useMutation({
    mutationFn: ({
      teacherId,
      slotId,
    }: {
      teacherId: string;
      slotId: string;
    }) => teachersApi.deleteAvailability(teacherId, slotId),
    onSuccess: (_, variables) => {
      invalidateSlotCaches([userKeys.teacherAvailability(variables.teacherId)]);
    },
  });
}

export function useBulkSetTeacherAvailability() {
  const invalidateSlotCaches = useInvalidateSlotCaches();

  return useMutation({
    mutationFn: ({
      teacherId,
      slots,
    }: {
      teacherId: string;
      slots: Array<{ dayOfWeek: number; startTime: string; endTime: string }>;
    }) => teachersApi.bulkSetAvailability(teacherId, slots),
    onSuccess: (_, variables) => {
      invalidateSlotCaches([userKeys.teacherAvailability(variables.teacherId)]);
    },
  });
}

/**
 * Get outstanding task counts grouped by teacher
 */
export function useTeacherTaskSummary() {
  return useQuery({
    queryKey: userKeys.teacherTaskSummary(),
    queryFn: () => teachersApi.getTaskSummary(),
  });
}

export function useTaskReset() {
  return useQuery({
    queryKey: userKeys.teacherTaskReset(),
    queryFn: () => teachersApi.getTaskReset(),
  });
}

/**
 * Reset Teacher Tasks — write off outstanding incomplete-note tasks up to a
 * cutoff (default: now). On success, refresh every surface that shows a task
 * count: the teacher task-summary badges, the watermark display, and the admin
 * dashboard stats.
 */
export function useResetTeacherTasks() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data?: { cutoffDate?: string; reason?: string }) =>
      teachersApi.resetTasks(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.teacherTaskSummary() });
      queryClient.invalidateQueries({ queryKey: userKeys.teacherTaskReset() });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      enqueueSnackbar('Teacher tasks reset — counters cleared', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to reset teacher tasks', { variant: 'error' });
    },
  });
}

// ===========================================
// PARENTS HOOKS
// ===========================================

export function useParents() {
  return useQuery({
    queryKey: userKeys.parents(),
    queryFn: parentsApi.getAll,
  });
}

/**
 * Family-centric admin list. One row per Family, with the primary parent
 * surfaced and secondary parents available for inline expansion.
 */
export function useFamiliesList() {
  return useQuery({
    queryKey: userKeys.familiesAdminList(),
    queryFn: familiesApi.getAdminList,
  });
}

export function useParent(id: string) {
  return useQuery({
    queryKey: userKeys.parent(id),
    queryFn: () => parentsApi.getById(id),
    enabled: !!id,
  });
}

export function useFamilyDetails(id: string | null) {
  return useQuery({
    queryKey: [...userKeys.parent(id || ''), 'details'],
    queryFn: () => parentsApi.getDetails(id!),
    enabled: !!id,
  });
}

/**
 * Messageable members of a family, fetched lazily.
 *
 * `enabled` mirrors `useLessonClassMembers`: the list is only fetched when the message
 * modal actually opens, so browsing the families table costs nothing.
 */
export function useFamilyMembers(familyId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [...userKeys.families(), familyId, 'members'],
    queryFn: () => familiesApi.getMembers(familyId!),
    enabled: !!familyId && enabled,
  });
}

export function useCreateParent() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: CreateParentData) => parentsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.parents() });
      queryClient.invalidateQueries({ queryKey: userKeys.families() });
    },
    // #588: a failed Add Family used to be silent.
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      enqueueSnackbar(error.response?.data?.message || 'Failed to add family', {
        variant: 'error',
      });
    },
  });
}

// -------------------------------------------
// Send login link (#589)
// -------------------------------------------

/**
 * The request did not get the server's own answer: no response at all, or a gateway's (nginx
 * answers an HTML 504 after 60 s while the server is still waiting on the Mailgun budget, then
 * sends). The email may still go, and a second press would kill that link (Phase 4 /qa L4,
 * Phase 5 /qa M1).
 */
export const LOGIN_LINK_UNFINISHED_MESSAGE =
  "The request didn't finish. The email may still have been sent: check with them before pressing again.";

export type LoginLinkError = Error & {
  response?: { status?: number; data?: { message?: unknown; details?: { code?: unknown } } | string };
};

function serverMessage(error: LoginLinkError): string | undefined {
  const data = error.response?.data;
  return data && typeof data === 'object' && typeof data.message === 'string' && data.message
    ? data.message
    : undefined;
}

export function loginLinkErrorMessage(error: LoginLinkError): string {
  const message = serverMessage(error);
  if (message) return message;
  if (!error.response || (error.response.status ?? 0) >= 500) return LOGIN_LINK_UNFINISHED_MESSAGE;
  return 'The login link could not be sent.';
}

export function useSendParentLoginLink() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (parentId: string) => parentsApi.sendLoginLink(parentId),
    onSuccess: ({ sentTo }) => {
      queryClient.invalidateQueries({ queryKey: userKeys.parents() });
      queryClient.invalidateQueries({ queryKey: userKeys.families() });
      enqueueSnackbar(`Login link sent to ${sentTo}. It works for 7 days.`, { variant: 'success' });
    },
    onError: (error: LoginLinkError) => {
      enqueueSnackbar(loginLinkErrorMessage(error), { variant: 'error' });
    },
  });
}

export function useSendTeacherLoginLink() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (teacherId: string) => teachersApi.sendLoginLink(teacherId),
    onSuccess: ({ sentTo }) => {
      queryClient.invalidateQueries({ queryKey: userKeys.teachers() });
      enqueueSnackbar(`Login link sent to ${sentTo}. It works for 7 days.`, { variant: 'success' });
    },
    onError: (error: LoginLinkError) => {
      enqueueSnackbar(loginLinkErrorMessage(error), { variant: 'error' });
    },
  });
}

export function useUpdateParent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateParentData;
    }) => parentsApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.parents() });
      queryClient.invalidateQueries({ queryKey: userKeys.parent(variables.id) });
      queryClient.invalidateQueries({ queryKey: userKeys.families() });
    },
  });
}

export function useDeleteParent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => parentsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.parents() });
      queryClient.invalidateQueries({ queryKey: userKeys.families() });
    },
  });
}

// ===========================================
// STUDENTS HOOKS
// ===========================================

/**
 * Get all students
 * Note: studentsApi already extracts .data from ApiResponse wrapper
 */
export function useStudents() {
  return useQuery({
    queryKey: userKeys.students(),
    queryFn: studentsApi.getAll,
  });
}

/**
 * Get a single student by ID
 * Note: studentsApi already extracts .data from ApiResponse wrapper
 */
export function useStudent(id: string) {
  return useQuery({
    queryKey: userKeys.student(id),
    queryFn: () => studentsApi.getById(id),
    enabled: !!id,
  });
}

/**
 * Get enrollments for a student
 *
 * @param termFilter 'current' (default) returns enrollments in current/future
 *                   terms only; 'all' includes past terms too.
 */
export function useStudentEnrollments(
  studentId: string | null,
  termFilter: 'current' | 'all' = 'current'
) {
  return useQuery({
    queryKey: [...userKeys.student(studentId || ''), 'enrollments', termFilter],
    queryFn: () => studentsApi.getEnrollments(studentId!, termFilter),
    enabled: !!studentId,
  });
}

export function useCreateStudent() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: {
      firstName: string;
      lastName: string;
      birthDate?: string;
      familyId?: string;
      email?: string;
      phone?: string;
      notes?: string;
    }) => studentsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.students() });
      queryClient.invalidateQueries({ queryKey: userKeys.families() });
      enqueueSnackbar('Student created successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to create student';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useUpdateStudent() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<{
        firstName: string;
        lastName: string;
        birthDate: string | null;
        familyId: string | null;
        email: string | null;
        phone: string | null;
        notes: string | null;
        isActive: boolean;
        status: 'NEW' | 'ACTIVE' | 'INACTIVE';
      }>;
    }) => studentsApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.students() });
      queryClient.invalidateQueries({ queryKey: userKeys.student(variables.id) });
      queryClient.invalidateQueries({ queryKey: userKeys.families() });
      enqueueSnackbar('Student updated successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to update student';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useDeleteStudent() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => studentsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.students() });
      enqueueSnackbar('Student deleted successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to delete student';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useAssignStudentToFamily() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ studentId, familyId }: { studentId: string; familyId: string }) =>
      studentsApi.assignToFamily(studentId, familyId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.student(variables.studentId) });
      queryClient.invalidateQueries({ queryKey: userKeys.students() });
      queryClient.invalidateQueries({ queryKey: userKeys.families() });
    },
  });
}

export function useRemoveStudentFromFamily() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (studentId: string) => studentsApi.removeFromFamily(studentId),
    onSuccess: (_, studentId) => {
      queryClient.invalidateQueries({ queryKey: userKeys.student(studentId) });
      queryClient.invalidateQueries({ queryKey: userKeys.students() });
      queryClient.invalidateQueries({ queryKey: userKeys.families() });
    },
  });
}

// ===========================================
// FAMILIES HOOKS
// ===========================================

export function useFamilies() {
  return useQuery({
    queryKey: userKeys.families(),
    queryFn: familiesApi.getAll,
  });
}

export function useFamily(id: string) {
  return useQuery({
    queryKey: userKeys.family(id),
    queryFn: () => familiesApi.getById(id),
    enabled: !!id,
  });
}

export function useFamilyImpact(id: string | null | undefined) {
  return useQuery({
    queryKey: userKeys.familyImpact(id || ''),
    queryFn: () => familiesApi.getImpact(id!),
    enabled: !!id,
  });
}

export function useCreateFamily() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string }) => familiesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.families() });
    },
  });
}

export function useUpdateFamily() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<{ name: string; isActive: boolean }>;
    }) => familiesApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.families() });
      queryClient.invalidateQueries({ queryKey: userKeys.family(variables.id) });
    },
  });
}

export function useDeleteFamily() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => familiesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.families() });
    },
  });
}

export function useAddStudentToFamily() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ familyId, studentId }: { familyId: string; studentId: string }) =>
      familiesApi.addStudent(familyId, studentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.family(variables.familyId) });
      queryClient.invalidateQueries({ queryKey: userKeys.families() });
      queryClient.invalidateQueries({ queryKey: userKeys.students() });
    },
  });
}

export function useRemoveStudentFromFamilyById() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ familyId, studentId }: { familyId: string; studentId: string }) =>
      familiesApi.removeStudent(familyId, studentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.family(variables.familyId) });
      queryClient.invalidateQueries({ queryKey: userKeys.families() });
      queryClient.invalidateQueries({ queryKey: userKeys.students() });
    },
  });
}

export function useAddParentToFamily() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ familyId, parentId }: { familyId: string; parentId: string }) =>
      familiesApi.addParent(familyId, parentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.family(variables.familyId) });
      queryClient.invalidateQueries({ queryKey: userKeys.families() });
      queryClient.invalidateQueries({ queryKey: userKeys.parents() });
    },
  });
}

export function useRemoveParentFromFamily() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ familyId, parentId }: { familyId: string; parentId: string }) =>
      familiesApi.removeParent(familyId, parentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.family(variables.familyId) });
      queryClient.invalidateQueries({ queryKey: userKeys.families() });
      queryClient.invalidateQueries({ queryKey: userKeys.parents() });
    },
  });
}

// ===========================================
// PARENT PROFILE HOOKS (Self-Service)
// ===========================================

/**
 * Get current parent's profile (contact information)
 */
export function useParentProfile() {
  return useQuery({
    queryKey: userKeys.parentProfile(),
    queryFn: parentProfileApi.getProfile,
  });
}

/**
 * Update current parent's profile (contact information)
 */
export function useUpdateParentProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateParentProfileData) => parentProfileApi.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.parentProfile() });
    },
  });
}
